import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import { useDeviceStore } from '../../stores/deviceStore';

import { ShowAmbientSensors } from './AmbientSensors';
import { ShowChamberSensors } from './ChamberSensors';
import { MeasurementTimer } from './MeasurementTimer';

/**
 * @param {import('../../types/DeviceDescriptor').DeviceDescriptor} deviceDescriptor
 * @returns {import('./ChamberSensors').ChamberSensors}
 */
function getChamberSensors(deviceDescriptor) {
  // O2 controller sensors - each controller has one of each sensor
  const o2temperatureSensor = deviceDescriptor.o2Controllers.map(
    (controller) => controller.o2TemperatureSensor,
  );
  const o2statusSensor = deviceDescriptor.o2Controllers.map(
    (controller) => controller.o2StatusSensor,
  );
  const o2Sensor = deviceDescriptor.o2Controllers.map((controller) => controller.o2Sensor);

  // Temperature controller sensors - each controller has arrays of sensors
  // Use flatMap to flatten the nested arrays
  const chamberTemperatureSensor = deviceDescriptor.temperatureControllers.flatMap(
    (controller) => controller.chamberSensors,
  );
  const chamberHeatsinkTemperatureSensor = deviceDescriptor.temperatureControllers.flatMap(
    (controller) => controller.heatsinkSensors,
  );
  const overtemperatureSensor = deviceDescriptor.temperatureControllers.flatMap(
    (controller) => controller.overtemperatureSensors,
  );
  const heatingPowerSensor = deviceDescriptor.temperatureControllers.flatMap(
    (controller) => controller.heatingPowerSensors,
  );

  return {
    o2temperatureSensor,
    o2statusSensor,
    o2Sensor,
    chamberTemperatureSensor,
    chamberHeatsinkTemperatureSensor,
    overtemperatureSensor,
    heatingPowerSensor,
  };
}

/**
 * @param {import('../../types/DeviceDescriptor').DeviceDescriptor} deviceDescriptor
 * @returns {import('./AmbientSensors').AmbientSensors}
 */
function getAmbientSensors(deviceDescriptor) {
  return {
    ambientTemperatureSensor: deviceDescriptor.mainController.ambientTemperatureSensor,
    ambientHumiditySensor: deviceDescriptor.mainController.ambientHumiditySensor,
    ambientPressureSensor: deviceDescriptor.mainController.ambientPressureSensor,
  };
}

export const DeviceSensorValues = () => {
  const deviceDescriptor = useDeviceStore((state) => state.deviceDescriptor);

  const ambientSensors = useMemo(() => {
    if (!deviceDescriptor) return null;
    return getAmbientSensors(deviceDescriptor);
  }, [deviceDescriptor]);

  const chamberSensors = useMemo(() => {
    if (!deviceDescriptor) return null;
    return getChamberSensors(deviceDescriptor);
  }, [deviceDescriptor]);

  if (!deviceDescriptor || !ambientSensors || !chamberSensors) {
    return null;
  }

  return (
    <Box
      sx={{
        height: '5vh',
        maxHeight: '50px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '95%',
        margin: '0 auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <MeasurementTimer />
        <ShowAmbientSensors ambientSensors={ambientSensors} />
      </Box>
      <ShowChamberSensors chamberSensors={chamberSensors} />
    </Box>
  );
};
